"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { ChevronRight, Heart } from "lucide-react";
import { getFavourites } from "@/app/actions/favourites";
import { atlasLocationSupportsMode, type AtlasLocation, type AtlasMode } from "@/lib/conditions/atlas";
import { atlasForecastChange, buildAtlasWeek, parseAtlasSeen, rememberAtlasForecast, type AtlasSeenForecast } from "@/lib/conditions/atlas-week";
import { AtlasSkySymbol } from "./AtlasSkySymbol";
import styles from "./atlas.module.css";

const SEEN_KEY = "peakwave-atlas-seen-v1";
export function AtlasSavedPlaces({ locations, dates, mode, onSelect }: { locations: AtlasLocation[]; dates: string[]; mode: AtlasMode; onSelect: (location: AtlasLocation) => void }) {
  const [saved, setSaved] = useState<string[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "anonymous" | "error">("loading");
  const [attempt, setAttempt] = useState(0);
  const [previous, setPrevious] = useState<AtlasSeenForecast[]>([]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const signedIn = /(^|;\s*)sb-[^=]*-auth-token(\.\d+)?=/.test(document.cookie);
      try {
        const rows = await (signedIn ? getFavourites() : Promise.resolve(null));
        if (cancelled) return;
        if (!rows) { setStatus("anonymous"); return; }
        const slugs = rows.map((row) => row.spot_id);
        setSaved(slugs);
        setStatus("ready");
        try {
          setPrevious(parseAtlasSeen(localStorage.getItem(SEEN_KEY)));
        } catch { /* Public forecast comparisons are optional in private browsing. */ }
      } catch { if (!cancelled) setStatus("error"); }
    };
    void load();
    return () => { cancelled = true; };
  }, [attempt]);
  useEffect(() => {
    if (status !== "ready") return;
    try {
      const next = locations.filter((location) => saved.includes(location.slug)).slice(0, 20).map(rememberAtlasForecast);
      localStorage.setItem(SEEN_KEY, JSON.stringify(next));
    } catch { /* Unavailable browser storage does not prevent forecast browsing. */ }
  }, [locations, saved, status]);
  const places = saved.flatMap((slug) => { const location = locations.find((item) => item.slug === slug); return location && atlasLocationSupportsMode(location, mode) ? [location] : []; });
  return <div className={styles.savedPlaces}>
    <div className={styles.savedIntro}><Heart size={20} /><h2>Your places this week</h2><p>Your saved beaches, resorts and sailing spots, together.</p></div>
    {status === "loading" && <p role="status">Loading your saved places…</p>}
    {status === "error" && <><p role="alert">Your saved places couldn’t load.</p><button className={styles.secondary} onClick={() => { setStatus("loading"); setAttempt((n) => n + 1); }}>Retry saved places</button></>}
    {status === "anonymous" && <><p>Sign in to see the places saved to your Companion. You can explore every forecast without an account.</p><Link className={styles.primary} href="/login?redirect=%2Fconditions%2Fglobal%3Fpanel%3Dsaved">Sign in</Link></>}
    {status === "ready" && !places.length && <p>{saved.length ? "No saved places have forecasts for this sport in the atlas. Try Overview or explore another place." : "Open a beach or resort and save it to your Companion. Its coming week will appear here."}</p>}
    {places.map((location) => <AtlasSavedForecast key={location.slug} location={location} previous={previous.find((item) => item.slug === location.slug)} dates={dates} mode={mode} onSelect={onSelect} />)}
    {status === "ready" && <p className={styles.savedFootnote}>Changes compare the same forecast dates and sport with your last visit on this device. A new scoring version starts a fresh comparison.</p>}
  </div>;
}

export function AtlasSavedForecast({ location, previous, dates, mode, onSelect }: { location: AtlasLocation; previous?: AtlasSeenForecast; dates: string[]; mode: AtlasMode; onSelect: (location: AtlasLocation) => void }) {
  const week = buildAtlasWeek(location, dates, mode);
  const change = atlasForecastChange(location, previous, dates, mode);
  const day = week.days.find((item) => item.best) ?? week.days[0];
  return <div className={styles.savedPlace}>
    <button onClick={() => onSelect(location)}><AtlasSkySymbol onLight kind={day.sky} /><span><strong>{location.name}</strong><small>{location.country}</small></span><ChevronRight size={17} /></button>
    <p>{week.summary}</p>{change && <p className={styles.forecastChange}>{change}</p>}
  </div>;
}
